import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, Users, Shield, Award, Music, Drama, Theater, Tent, Palette, Trophy, Megaphone, Baby } from 'lucide-react';
import SEO, { breadcrumbSchema } from '@/components/SEO';
import CTASection from '@/components/CTASection';
import heroImg from '@/assets/artistes-hero.jpg';

const categories = [
  { name: 'Animateur Micro', slug: 'animateur-micro', icon: Megaphone, tagline: "L'âme et la voix de vos événements !", items: ['Animateur événementiel', 'Animateur de soirée', 'Maître de cérémonie'] },
  { name: 'Art de la Musique', slug: 'musique-ambiance', icon: Music, tagline: 'Donnez du rythme à vos événements !', items: ['DJ, DJ Sax, DJ Violon', 'Groupe Pop, Rock, Jazz, Disco, Country, Lounge, Celtic', 'Instruments', 'Tribute', 'Variété française & internationale'] },
  { name: 'Art de la Rue', slug: 'art-de-la-rue', icon: Theater, tagline: "Quand la rue s'anime, les émotions prennent vie.", items: ['Cascadeur', 'Chars', 'Cirque de rue', 'Échassiers', 'Fanfare', 'Feu & Pyrotechnie & LED', 'Jongleurs Saltimbanque', 'Mascottes', 'Médiéval', 'Mime, Statue vivante, Théâtre', 'Musicien ambianceur de rue solo', 'Troupes de danses — Parade — Défilé'] },
  { name: 'Art de la Scène', slug: 'art-de-la-scene', icon: Drama, tagline: "Quand l'art s'invite, le cœur du public s'éveille.", items: ['Magie Grande Illusion, Close Up, Mentaliste', 'Cabaret — Revue — Music Hall', 'Humour, Stand Up, Improvisation', 'Danseur — Danseuse', 'Comédien infiltré & Faux serveur', 'Hypnotiseur', 'Burlesque', 'Ventriloquie', 'Sosie physique et/ou voix', 'Imitateur', 'LED', 'Comédies musicales'] },
  { name: 'Art du Cirque', slug: 'art-du-cirque', icon: Tent, tagline: "Des numéros spectaculaires pour des moments d'exception.", items: ['Circassiens', 'Acrobates', 'Artistes aériens', 'Échassiers', 'Jongleurs', 'Contorsionnistes'] },
  { name: 'Arts Visuels', slug: 'arts-visuels', icon: Palette, tagline: 'Quand la créativité prend vie sous les yeux de votre public.', items: ['Body Painting', 'Caricaturiste', 'Silhouettiste', 'Speed Painting / Glitter'] },
  { name: 'Enfants', slug: 'animations-enfants-famille', icon: Baby, tagline: "L'univers magique des enfants au cœur de vos événements…", items: ['Atelier créatif', 'Chasse aux trésors', 'Cirque', 'Comédie musicale — Conte', 'Magie', 'Manège', 'Maquillages, Tatouages éphémères', 'Marionnettes', 'Père Noël', 'Sculptures de ballons', 'Structures gonflables', 'Ventriloques'] },
  { name: 'Sport Spectacle', slug: 'sport-spectacle', icon: Trophy, tagline: 'Des performances extrêmes pour des émotions intenses.', items: ['Basket', 'Football Freestyle', 'BMX', 'Double Dutch', 'Moto', 'Skate', 'Roller', 'Vélo', 'Trampoline', 'Trial Vélo & Moto', 'Arts martiaux'] },
];

const advantages = [
  { icon: Star, title: 'Sélection rigoureuse', desc: 'Chaque artiste est auditionné et validé pour son professionnalisme.' },
  { icon: Users, title: 'Réseau national', desc: 'Un catalogue de talents disponibles partout en France.' },
  { icon: Shield, title: 'Fiabilité garantie', desc: 'Artistes assurés, briefés et coordonnés par notre équipe.' },
  { icon: Award, title: 'Accompagnement complet', desc: 'Du casting à la prestation, nous gérons tout pour vous.' },
];

const recruitmentBenefits = [
  'Évaluation de votre profil artistique',
  'Mise en relation avec des organisateurs',
  'Intégration à notre catalogue d\'artistes',
  'Accompagnement à la professionnalisation',
  'Conseils scéniques et techniques',
  'Visibilité sur nos supports de communication',
];

const Artistes = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Artistes & Talents | Cygnature Production Troyes"
        description="Découvrez notre catalogue d'artistes pluridisciplinaires : musiciens, comédiens, danseurs, circassiens, magiciens et bien plus. Rejoignez notre sélection ou réservez un talent pour votre événement."
        path="/artistes"
        keywords={['catalogue artistes','booking artistes','talents événementiels','musiciens','magiciens','danseurs','circassiens']}
        jsonLd={[breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Artistes', path: '/artistes' }])]}
      />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <img src={heroImg} alt="Artiste sur scène" className="absolute inset-0 w-full h-full object-cover" width={1920} height={800} />
        <div className="absolute inset-0 bg-gradient-to-b from-petrol/80 via-petrol/60 to-petrol/90" />
        <div className="relative z-10 text-center px-6 py-20 md:py-28 max-w-4xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-3">Notre catalogue de talents</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
            Des artistes d'exception pour vos événements
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-8">
            Chanteurs, comédiens, magiciens, danseurs, circassiens, DJs et bien plus encore. Nous sélectionnons, auditons et produisons les meilleurs talents pour chaque univers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#catalogue" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
              Explorer le catalogue <ArrowRight size={15} />
            </a>
            <a href="#candidater" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/30 text-white font-heading font-medium text-sm hover:bg-white/10 transition-colors">
              Je suis artiste
            </a>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2 text-center">Pourquoi nous choisir</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-12 text-center">
            Une sélection exigeante, une qualité garantie
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((a) => (
              <div key={a.title} className="text-center p-6 rounded-2xl border border-border bg-card">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <a.icon size={22} className="text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2 text-sm">{a.title}</h3>
                <p className="text-xs text-muted-foreground">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Catalogue */}
      <section id="catalogue" className="px-6 lg:px-8 py-16 md:py-20 bg-petrol">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2 text-center">Notre catalogue</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-petrol-foreground mb-3 text-center">
            Des artistes pluridisciplinaires pour chaque univers
          </h2>
          <p className="text-sm text-petrol-foreground/60 max-w-2xl mx-auto text-center mb-12">
            Chaque artiste est auditionné, puis sélectionné pour son professionnalisme, sa fiabilité et la qualité de sa prestation.
          </p>

          <div className="space-y-6">
            {categories.map((cat) => (
              <div key={cat.slug} className="rounded-2xl border border-petrol-foreground/10 bg-petrol-foreground/5 p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                      <cat.icon size={20} className="text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-petrol-foreground">{cat.name}</h3>
                      <p className="text-xs text-petrol-foreground/50 italic">{cat.tagline}</p>
                    </div>
                  </div>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-petrol-foreground/70">
                      <Check size={12} className="text-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/services/${cat.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center gap-1.5 text-xs text-primary font-heading font-medium hover:gap-2.5 transition-all"
                >
                  Découvrir cet univers <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recrutement / Candidature */}
      <section id="candidater" className="px-6 lg:px-8 py-16 md:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Vous êtes artiste ?</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3 italic">
            Rejoignez notre sélection et développez votre visibilité
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
            Vous souhaitez intégrer notre catalogue ou bénéficier d'un accompagnement pour faire évoluer votre projet artistique ? Nous étudions chaque profil avec attention, puis nous vous orientons selon votre univers, votre potentiel et les opportunités adaptées à votre positionnement.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto mb-8">
            {recruitmentBenefits.map((item) => (
              <div key={item} className="flex items-start gap-2 text-left">
                <Check size={14} className="text-primary mt-0.5 shrink-0" />
                <span className="text-xs text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
          <Link
            to="/contact?subject=artiste"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Candidater <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Artistes;
